/**
 * All reference content on the docs page lives here as data, separated from
 * layout. Field names never appear loose in prose — every field, status and
 * grade is defined in exactly one table.
 */

export type Section = { id: string; title: string };

export const SECTIONS: readonly Section[] = [
  { id: 'overview', title: 'Overview' },
  { id: 'authentication', title: 'Authentication' },
  { id: 'quick-start', title: 'Quick start' },
  { id: 'post-identify', title: 'POST /v1/identify' },
  { id: 'get-job', title: 'GET /v1/jobs/{job_id}' },
  { id: 'result', title: 'The result object' },
  { id: 'confidence', title: 'Confidence grades' },
  { id: 'statuses', title: 'Status reference' },
  { id: 'examples', title: 'Sample responses' },
  { id: 'errors', title: 'HTTP errors' },
  { id: 'mcp', title: 'MCP for agents' },
  { id: 'billing', title: 'Billing' },
];

export type FieldRow = { name: string; type?: string; description: string };

/** Overview — the two endpoints. */
export const ENDPOINTS: readonly FieldRow[] = [
  {
    name: 'POST /v1/identify',
    description:
      'Submit a store URL. Returns the result immediately when one is already available, otherwise queues a scan.',
  },
  {
    name: 'GET /v1/jobs/{job_id}',
    description: 'Poll a queued scan until it succeeds or fails.',
  },
];

/** Authentication — connection settings. */
export const AUTH_SETTINGS: readonly FieldRow[] = [
  { name: 'Token endpoint', description: '`https://dev-rmiw5hh1oyorjysf.us.auth0.com/oauth/token`' },
  { name: 'Grant type', description: '`client_credentials`' },
  { name: 'Audience', description: '`https://api.acceptlens.com`' },
  { name: 'Header', description: '`Authorization: Bearer <token>` on every request' },
  { name: 'Token lifetime', description: '1 hour — request a new token rather than caching past `expires_in`' },
];

/** POST /v1/identify — request body. */
export const REQUEST_FIELDS: readonly FieldRow[] = [
  {
    name: 'url',
    type: 'string, required',
    description: 'Full URL of the store to check, e.g. `https://example.com`.',
  },
];

/** POST /v1/identify — response envelope (200 or 202). */
export const POST_RESPONSE_FIELDS: readonly FieldRow[] = [
  {
    name: 'job_id',
    type: 'string | null',
    description: 'Identifier of the queued scan. `null` on an immediate `200` — there is no job to poll.',
  },
  {
    name: 'status',
    type: 'string',
    description: '`succeeded` on an immediate `200`; `queued` when a scan was started.',
  },
  {
    name: 'result',
    type: 'object',
    description: 'The result object (below). Present only on an immediate `200`.',
  },
  {
    name: 'created_at',
    type: 'string (ISO 8601)',
    description: 'When the job was created. `202` only.',
  },
  {
    name: 'status_url',
    type: 'string',
    description: 'Path to poll: `/v1/jobs/{job_id}`. `202` only.',
  },
];

/** GET /v1/jobs/{job_id} — the job envelope. */
export const JOB_FIELDS: readonly FieldRow[] = [
  { name: 'job_id', type: 'string', description: 'The job identifier from the `202` response.' },
  {
    name: 'status',
    type: 'string',
    description: 'Lifecycle: `queued` → `running` → `succeeded` or `failed`.',
  },
  { name: 'created_at', type: 'string (ISO 8601)', description: 'When the job was created.' },
  {
    name: 'started_at',
    type: 'string (ISO 8601)',
    description: 'When a worker picked the job up. Absent while `queued`.',
  },
  {
    name: 'finished_at',
    type: 'string (ISO 8601)',
    description: 'When the scan ended. Present once `succeeded` or `failed`.',
  },
  {
    name: 'elapsed_s',
    type: 'number',
    description: 'Seconds since the worker started. Sent only while `running`.',
  },
  {
    name: 'result',
    type: 'object',
    description: 'The result object (below). Present only when `succeeded`.',
  },
  {
    name: 'error',
    type: 'string',
    description:
      '`internal_error` or `stale_job`. Present only when `failed`; both are retryable — submit the URL again.',
  },
  {
    name: 'message',
    type: 'string',
    description: 'Human-readable failure detail. Present only when `failed`.',
  },
];

/** The result object — the public contract, nothing more. */
export const RESULT_FIELDS: readonly FieldRow[] = [
  { name: 'url_input', type: 'string', description: 'The URL you submitted, as received.' },
  {
    name: 'url_final',
    type: 'string | null',
    description:
      'Where the store actually resolved — after redirects, or a `shop.` / `store.` subdomain that hosts the storefront. `null` when unreachable.',
  },
  {
    name: 'probed_at',
    type: 'string (ISO 8601)',
    description: "When the storefront was read — the result's as-of date. Check it when freshness matters.",
  },
  {
    name: 'status',
    type: 'string',
    description: 'The scan outcome — one of the fourteen values in the status reference below.',
  },
  {
    name: 'payments',
    type: '{ method, confidence }[]',
    description:
      'Methods the store accepts. `method` is a snake_case identifier — `mada`, `visa`, `mastercard`, `apple_pay`, `stc_pay`, `tamara`, `tabby`, `cod`, `paypal`, … `confidence` is `high` or `medium` (below). Only an `active` store carries entries here.',
  },
  {
    name: 'bnpl_advertised',
    type: 'string[]',
    description:
      'Buy-now-pay-later providers advertised on the page (widget, badge, script) but not confirmed as accepted. Never overlaps `payments`.',
  },
];

/** Confidence — what backs an accepted method. */
export const CONFIDENCE_GRADES: readonly FieldRow[] = [
  { name: 'high', description: "Read from the platform's own configuration or the live checkout." },
  {
    name: 'medium',
    description: 'Read from storefront evidence — a payment plugin, gateway script or footer logo.',
  },
];

/** Every value of the result's status field. */
export const STATUSES: readonly FieldRow[] = [
  { name: 'active', description: 'Store is live and has payment methods configured.' },
  { name: 'empty_payments_store', description: 'Store is live but has zero payment methods configured.' },
  { name: 'deleted_store', description: 'Store no longer exists on the platform.' },
  {
    name: 'inactive_or_maintenance',
    description: 'Store is present but not accessible (maintenance, suspended).',
  },
  {
    name: 'platform_not_supported',
    description: 'The store runs on a platform whose checkout cannot be read yet.',
  },
  {
    name: 'platform_unknown',
    description: 'Looks like a store, but no known storefront stack was recognised.',
  },
  {
    name: 'platform_identified_no_payments',
    description: 'Storefront stack recognised but no accepted payment signals found.',
  },
  {
    name: 'not_a_merchant',
    description: 'URL is not a store (aggregator, social profile, provider portal).',
  },
  { name: 'unreachable_dns', description: 'DNS resolution failed.' },
  { name: 'unreachable_ssl', description: 'SSL/TLS handshake failed.' },
  { name: 'unreachable_anti_bot', description: 'Anti-bot challenge blocked the page.' },
  { name: 'unreachable_geo', description: 'Geo or legal block prevented access.' },
  {
    name: 'unreachable_other',
    description: "Network error that doesn't fit the above (transient catch-all).",
  },
  { name: 'error', description: 'Detection error — inconclusive, a retry may help.' },
];

/** HTTP error responses. */
export const HTTP_ERRORS: readonly FieldRow[] = [
  {
    name: '400',
    type: 'invalid_input',
    description: 'Request body is missing or malformed.',
  },
  {
    name: '401',
    type: 'unauthorized',
    description: 'Missing, malformed, expired, or wrong-audience access token.',
  },
  {
    name: '404',
    type: 'not_found',
    description: 'Unknown or expired job, or a job belonging to another client.',
  },
  {
    name: '500',
    type: 'internal_error',
    description: 'Transient backend failure. Retry with backoff.',
  },
];

/** The two MCP tools. */
export const MCP_TOOLS: readonly FieldRow[] = [
  {
    name: 'check_payment_methods',
    type: 'url',
    description: 'Returns the result when one is already available, or a `job_id` to collect later.',
  },
  {
    name: 'get_scan_result',
    type: 'job_id',
    description: 'Returns the finished result, an in-flight status, or an error. Checks once — never waits.',
  },
];

/** Billing rules. */
export const BILLING_RULES: readonly string[] = [
  'One billable call per `(user, domain, ISO week)` — the first conclusive result for a domain in a week.',
  'Repeat calls for the same domain within that week are free, so re-checking is safe.',
  'Inconclusive outcomes (`unreachable_*`, `error`) are never billed.',
];
